import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  SearchIcon,
  TrendingUpIcon,
  UserCheckIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';

export function Landing() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/95 shadow-sm">
        <div className="container mx-auto">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <span className="font-bold text-xl text-gray-900">
                Brand Check
              </span>
            </Link>
            <nav className="flex items-center space-x-4">
              <Button
                variant="ghost"
                asChild
                className="text-gray-600 hover:text-gray-900"
              >
                <a href="#pricing">Pricing</a>
              </Button>
              <Button
                variant="ghost"
                asChild
                className="text-gray-600 hover:text-gray-900"
              >
                <Link to="/login">Login</Link>
              </Button>
              <Button asChild>
                <Link to="/signup">Analyze your first influencer</Link>
              </Button>
            </nav>
          </div>
        </div>
      </header>

      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-300/10 to-purple-300/10"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col items-center text-center gap-8">
            <div className="flex flex-row items-center gap-2 px-4 py-2 text-sm font-medium text-blue-800 border-0">
              <TrendingUpIcon className="w-4 h-4 mr-2" />
              Stop Brand Conflicts Before They Happen
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight max-w-4xl bg-gradient-to-br from-gray-800 to-gray-900 bg-clip-text text-transparent leading-tight opacity-95">
              Instantly Check If An Influencer Worked With Your Competitors
            </h1>

            <p className="text-lg md:text-xl text-secondary-foreground max-w-3xl leading-relaxed">
              {/* <p className="mt-6 text-lg text-muted-foreground max-w-3xl"> */}
              Enter an influencer handle and a timeframe, and we'll scan every
              Instagram and TikTok post in that window and use AI to find{' '}
              <span className="font-semibold">
                collaborations with your competitors
              </span>{' '}
              and any brands you flag. Get detailed results with the exact
              posts, dates, and brands.
              {/* </p> */}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="xl" asChild>
                <Link to="/signup">Analyze your first influencer</Link>
              </Button>
            </div>

            <div className="max-w-[600px] flex flex-row flex-wrap items-center justify-center gap-4 text-sm text-gray-500">
              <div className="flex flex-row gap-2 md:text-nowrap">
                <div className="h-lh flex items-center">
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                </div>
                Uncover hidden partnerships
              </div>
              <div className="flex flex-row gap-2 md:text-nowrap">
                <div className="h-lh flex items-center">
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                </div>
                Results in 60 seconds
              </div>
              <div className="flex flex-row gap-2 md:text-nowrap">
                <div className="h-lh flex items-center">
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                </div>
                Instagram & TikTok
              </div>
              <div className="flex flex-row gap-2 md:text-nowrap">
                <div className="h-lh flex items-center">
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                </div>
                Analyze dozens of profiles in minutes
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              AI That Never Misses a Brand Association
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our advanced AI scans every post, collaboration, and brand mention
              to give you complete confidence in your influencer selections.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-11 h-11 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center shadow">
                    <SearchIcon className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900">
                    Deep Content Analysis
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Scans captions, hashtags, collaboration posts, and visual
                    content to detect paid partnerships and brand associations.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-11 h-11 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg flex items-center justify-center shadow">
                    <EyeIcon className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900">
                    Competitor Intelligence
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Identifies direct competitors, indirect competitors, and
                    related brands{' '}
                    <span className="font-bold">
                      based on your campaign parameters
                    </span>{' '}
                    and industry knowledge.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-11 h-11 bg-gradient-to-br from-green-100 to-green-200 rounded-lg flex items-center justify-center shadow">
                    <ClockIcon className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900">
                    Flexible Lookback Periods
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Customize analysis timeframes from weeks to years based on
                    your client's exclusivity requirements.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative">
              <Card className="p-6 shadow-2xl border-0 bg-white">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-900">
                      @fashioninfluencer
                    </span>
                    <Badge className="bg-red-100 text-red-800 hover:bg-red-100 px-3 py-1 text-sm font-semibold">
                      Conflict Found
                    </Badge>
                  </div>
                  <div className="border-l-4 border-red-500 pl-4 space-y-2 bg-red-50 py-3 rounded">
                    <p className="font-semibold text-red-900">
                      Nike Partnership Detected
                    </p>
                    <p className="text-sm text-red-700">Posted 4 months ago</p>
                    <p className="text-sm text-red-700 font-mono bg-red-100 p-2 rounded">
                      "#ad #nike #justdoit" - Sponsored content collaboration
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              One Missed Brand Conflict Can Cost Everything
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Manual vetting puts your agency at risk. Hidden partnerships,
              missed collaborations, and overlooked brand associations can
              destroy client trust and campaign success.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="border border-red-200 hover:shadow-lg transition-shadow duration-300 bg-white relative overflow-hidden">
              <CardContent>
                <div className="flex items-start space-x-4">
                  <div className="bg-red-100 p-2.5 rounded-lg flex-shrink-0">
                    <AlertTriangleIcon className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Prevent Costly Errors
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Missing a single competitor partnership can result in
                      campaign failures, client disputes, and damaged
                      relationships.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-orange-200 hover:shadow-lg transition-shadow duration-300 bg-white relative overflow-hidden">
              <CardContent>
                <div className="flex items-start space-x-4">
                  <div className="bg-orange-100 p-2.5 rounded-lg flex-shrink-0">
                    <ClockIcon className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Save Hours of Manual Vetting Time
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Manually checking 6+ months of posts across Instagram and
                      TikTok takes hours for multiple influencers. It doesn't
                      scale for large campaigns.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-blue-200 hover:shadow-lg transition-shadow duration-300 bg-white relative overflow-hidden">
              <CardContent>
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-2.5 rounded-lg flex-shrink-0">
                    <UserCheckIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Never Miss Hidden Paid Content
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Subtle brand partnerships, collaboration posts, and
                      undisclosed sponsorships are easy to miss but deadly for
                      brand safety.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              How It Works
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Get from brief to confident hiring decisions in 3 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                1
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">
                Set Campaign Parameters
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Define your brand, competitors to avoid, target regions, and
                lookback period. Our AI understands your exclusivity
                requirements.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                2
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">
                Add Influencers
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Input Instagram and TikTok handles individually or in bulk. Our
                system immediately begins analyzing their content history.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                3
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">
                Get Conflict Reports
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Receive detailed findings with post evidence, brand mentions,
                dates, and clear recommendations for each influencer.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about Brand Check
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="accuracy">
              <AccordionTrigger>
                How accurate is the AI detection?
              </AccordionTrigger>
              <AccordionContent>
                Our AI achieves 95%+ accuracy in detecting paid partnerships and
                brand associations. Our advanced AI thoroughly examines every
                element of each post - captions, images, videos, collaboration
                indicators, paid partnership disclosures, and even the most
                subtle promotional cues - to uncover hidden brand relationships
                that could impact your campaigns.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="platforms">
              <AccordionTrigger>What platforms are supported?</AccordionTrigger>
              <AccordionContent>
                Currently, Brand Check supports{' '}
                <span className="font-semibold">Instagram and TikTok</span>{' '}
                analysis. We're working on adding YouTube, Twitter, and other
                platforms based on user demand.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="timeframe">
              <AccordionTrigger>How far back can it analyze?</AccordionTrigger>
              <AccordionContent>
                We can analyze content{' '}
                <span className="font-semibold">
                  for any custom period you specify
                </span>{' '}
                - whether that's specific weeks, months, or years - to perfectly
                match your client's exclusivity requirements and campaign needs.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="privacy">
              <AccordionTrigger>
                Is influencer data kept private?
              </AccordionTrigger>
              <AccordionContent>
                Yes, all data is encrypted and handled according to GDPR and
                privacy best practices. We only analyze publicly available
                content and never store personal information beyond what's
                necessary for the analysis.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="speed">
              <AccordionTrigger>
                How quickly are results delivered?
              </AccordionTrigger>
              <AccordionContent>
                Most analyses complete{' '}
                <span className="font-semibold">within 60 seconds</span>.
                Complex profiles with extensive content history may take up to 5
                minutes. You'll see real-time progress updates during the
                analysis.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="pricing">
              <AccordionTrigger>What does it cost?</AccordionTrigger>
              <AccordionContent>
                Pricing{' '}
                <span className="font-semibold">
                  depends on how much content the influencer has posted
                </span>{' '}
                and how far back you analyze. Most analyses cost between{' '}
                <span className="font-semibold">
                  $0.20 - $2.00 per influencer
                </span>
                .
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      <section
        className="py-16 bg-white"
        id="pricing"
        style={{ scrollMarginTop: '4rem' }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Start analyzing dozens of influencers immediately
            </p>
          </div>

          <div className="max-w-md mx-auto">
            <Card className="border-2 border-blue-500 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 bg-blue-500 text-white text-center py-2 text-sm font-semibold">
                Most Popular
              </div>
              <CardContent className="pt-10 pb-8">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <span className="text-4xl font-bold text-gray-900">
                      $100
                    </span>
                    <span className="text-gray-600">/month</span>
                  </div>
                  <div className="mb-6">
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100 px-3 py-1 text-sm font-semibold mb-3">
                      Includes $50 Usage Credits
                    </Badge>
                    <p className="text-sm text-gray-600">
                      Analyze 25-250 influencers per month
                      <br />
                      <span className="text-xs">
                        (Most analyses cost $0.20 - $2.00 per influencer)
                      </span>
                    </p>
                  </div>

                  <div className="space-y-3 mb-8 text-left">
                    <div className="flex items-center gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">
                        Instagram & TikTok analysis
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">
                        Custom competitor lists
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">
                        Flexible lookback periods
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">
                        Detailed conflict reports
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">
                        Team collaboration (
                        <span className="font-semibold underline">
                          unlimited users
                        </span>
                        )
                      </span>
                    </div>
                  </div>

                  <Button size="lg" className="w-full" asChild>
                    <Link to="/signup">Start Your Free Trial</Link>
                  </Button>

                  <div className="mt-4 space-y-1 text-xs text-gray-500">
                    <p>• No setup fees • Cancel anytime</p>
                    <p>• Additional usage billed per analysis</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Stop Wasting Hours on Manual Research
          </h2>
          <p className="text-lg mb-8 opacity-90 leading-relaxed">
            Get instant, comprehensive influencer analysis in seconds. What used
            to take hours of manual digging now happens with a single click.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button size="xl" variant="secondary" asChild>
              <Link to="/signup">Analyze your first influencer</Link>
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 text-sm opacity-90">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-4 h-4" />
              Results in under 60 seconds
            </div>
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-4 h-4" />
              Zero manual research
            </div>
          </div>
        </div>
      </section>

      <footer className="py-12 border-t bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <div className="flex items-center mb-4 md:mb-0">
              <span className="font-bold text-xl text-gray-900">
                Brand Check
              </span>
            </div>
            <nav className="flex space-x-6 text-gray-600">
              <Button
                variant="link"
                className="p-0 h-auto font-normal text-gray-600 hover:text-gray-900"
                asChild
              >
                <Link to="/privacy">Privacy Policy</Link>
              </Button>
              <Button
                variant="link"
                className="p-0 h-auto font-normal text-gray-600 hover:text-gray-900"
                asChild
              >
                <Link to="/tos">Terms of Service</Link>
              </Button>
            </nav>
          </div>
          <div className="text-center text-gray-500 border-t pt-6 text-sm">
            © 2025 Brand Check. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
